/** @jsxRuntime classic */
/** @jsx jsx */

import { jsx } from '@emotion/core';
import PropTypes from 'prop-types';
import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { date as moment } from "../../../utils";
import { isAfterLimit, isBeforeLimit, onUpdateCalendar, renderHeader, renderNavigator, isValidDate, generateCalendarCSS } from "./function";
const Calendar = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(({
  actions,
  className,
  defaultValue,
  displayAnotherMonth,
  max,
  min,
  onClick,
  value,
  ...props
}, reference) => {
  // const [id] = useState(randomString(6, { allowSymbol: false }));
  const unique = {
    container: 'DGN-UI-Calendar',
    icon: 'DGN-UI-Calendar-Icon',
    navigator: {
      navigator: 'DGN-UI-Calendar-Navigator',
      around: 'DGN-UI-Calendar-Navigator-Around',
      center: 'DGN-UI-Calendar-Navigator-Center'
    },
    table: {
      container: 'DGN-UI-Calendar-Table-Container',
      table: 'DGN-UI-Calendar-Table-Table',
      header: 'DGN-UI-Calendar-Table-Header',
      raw: 'DGN-UI-Calendar-Table-Raw',
      data: 'DGN-UI-Calendar-Table-Data'
    },
    day: {
      day: 'DGN-UI-Calendar-Day',
      week: 'DGN-UI-Calendar-Week',
      today: 'DGN-UI-Calendar-Day-today',
      active: 'DGN-UI-Calendar-Day-active',
      limit: 'DGN-UI-Calendar-Day-limit'
    }
  };

  /**
   * START REFERENCE
   */
  const ref = useRef(null);
  const tableRef = useRef(null);
  const activeValue = useRef(null);
  const navigatorValue = useRef(null);
  useImperativeHandle(reference, () => ref.current);
  const [, setActiveValue] = useState(Date.now());
  const [, setNavigatorValue] = useState(Date.now());
  const navigatorRefs = {
    doubleLeft: useRef(null),
    doubleRight: useRef(null),
    left: useRef(null),
    right: useRef(null),
    content: useRef(null)
  };
  const firstDayOfMax = new Date(new Date(max).setDate(1)).setHours(0, 0, 0, 0);
  const firstDayOfMin = new Date(new Date(min).setDate(1)).setHours(0, 0, 0, 0);
  /**
   * END REFERENCE
   */

  /**
   * START FUNCTION
   */
  const onUpdateNavigatorValue = useCallback(value => {
    navigatorValue.current = value;
    setNavigatorValue();
  }, [navigatorValue]);
  const onUpdateActiveValue = useCallback(value => {
    activeValue.current = value;
    setActiveValue();
  }, [activeValue]);
  const onUpdate = value => {
    const newValue = new Date(value).setHours(0, 0, 0, 0);
    onUpdateActiveValue(newValue);
    onUpdateCalendar(newValue, navigatorRefs, min, max, tableRef.current, activeValue.current, unique, displayAnotherMonth, onDayClick, onUpdateNavigatorValue);
  };
  const getCurrentTime = () => {
    const time = new Date(navigatorValue.current);
    return [time.getDate(), time.getMonth(), time.getFullYear()];
  };
  const onDayClick = e => {
    const node = e.currentTarget;
    tableRef.current.querySelectorAll(`td.${unique.day.active}`).forEach(element => {
      element.classList.remove(unique.day.active);
    });
    node.classList.add(unique.day.active);
    const timeStamp = Number(node.dataset.time);
    const objNewDate = new Date(timeStamp);
    const _navigatorValue = new Date(navigatorValue.current);
    const objNewDateMonth = objNewDate.getMonth() + 1;
    const objNewDateYear = objNewDate.getFullYear();
    const navigatorMonth = _navigatorValue.getMonth() + 1;
    const navigatorYear = _navigatorValue.getFullYear();
    onUpdateActiveValue(objNewDate);
    if (objNewDateMonth > navigatorMonth || objNewDateYear > navigatorYear) setNextMonth();else if (objNewDateMonth < navigatorMonth || objNewDateYear < navigatorYear) setPrevMonth();
    !!onClick && onClick({
      value: objNewDate
    });
  };
  const setPrevYear = e => {
    const time = new Date(getCurrentTime()[2] - 1, getCurrentTime()[1]);
    if (min && isValidDate(min) && moment(time).diff(firstDayOfMin) < 0) {
      e.preventDefault();
    } else {
      onUpdateCalendar(time, navigatorRefs, min, max, tableRef.current, activeValue.current, unique, displayAnotherMonth, onDayClick, onUpdateNavigatorValue);
    }
  };
  const setNextYear = e => {
    const time = new Date(getCurrentTime()[2] + 1, getCurrentTime()[1]);
    if (max && isValidDate(max) && moment(time).diff(firstDayOfMax) > 0) {
      e.preventDefault();
    } else {
      onUpdateCalendar(time, navigatorRefs, min, max, tableRef.current, activeValue.current, unique, displayAnotherMonth, onDayClick, onUpdateNavigatorValue);
    }
  };
  const setPrevMonth = e => {
    const time = new Date(getCurrentTime()[2], getCurrentTime()[1] - 1);
    if (min && isValidDate(min) && moment(time).diff(firstDayOfMin) < 0) {
      e.preventDefault();
    } else {
      onUpdateCalendar(time, navigatorRefs, min, max, tableRef.current, activeValue.current, unique, displayAnotherMonth, onDayClick, onUpdateNavigatorValue);
    }
  };
  const setNextMonth = e => {
    const time = new Date(getCurrentTime()[2], getCurrentTime()[1] + 1);
    if (max && isValidDate(max) && moment(time).diff(firstDayOfMax) > 0) {
      e.preventDefault();
    } else {
      onUpdateCalendar(time, navigatorRefs, min, max, tableRef.current, activeValue.current, unique, displayAnotherMonth, onDayClick, onUpdateNavigatorValue);
    }
  };

  /**
   * END FUNCTION
   */

  /**
   * START EFFECT
   */
  useEffect(() => {
    if (defaultValue && defaultValue !== '' && isValidDate(defaultValue)) {
      if (isBeforeLimit(min, defaultValue)) {
        onUpdate(min);
      } else if (isAfterLimit(max, defaultValue)) {
        onUpdate(max);
      } else {
        onUpdate(defaultValue);
      }
    }
  }, []);
  useEffect(() => {
    if (value && value !== '' && isValidDate(value)) {
      if (isBeforeLimit(min, value)) {
        onUpdate(min);
      } else if (isAfterLimit(max, value)) {
        onUpdate(max);
      } else {
        onUpdate(value);
      }
    }
  }, [value]);
  useEffect(() => {
    if (min && isBeforeLimit(min, activeValue.current)) {
      onUpdate(min);
      !!onClick && onClick({
        value: new Date(min)
      });
    } else if (max && isAfterLimit(max, activeValue.current)) {
      onUpdate(max);
      !!onClick && onClick({
        value: new Date(max)
      });
    } else {
      onUpdate(activeValue.current);
    }
  }, [onClick, min, max]);
  /**
   * END FUNCTION
   */

  /**
   * START COMPONENT
   */
  const tableMemo = jsx("div", {
    className: unique.table.container
  }, jsx("table", {
    className: unique.table.table,
    ref: tableRef
  }, renderHeader(unique), jsx("tbody", null)));
  /**
   * END COMPONENT
   */
  return jsx("div", {
    ...props,
    className: [unique.container, className].join(' ').trim().replace(/\s+/g, ' '),
    css: generateCalendarCSS(unique),
    ref: ref
  }, renderNavigator(unique, navigatorRefs, setPrevYear, setPrevMonth, setNextMonth, setNextYear), tableMemo, actions);
}));
Calendar.defaultProps = {
  actions: null,
  defaultValue: new Date(),
  displayAnotherMonth: true
};
Calendar.propTypes = {
  /** the footer actions */
  actions: PropTypes.oneOfType([PropTypes.element, PropTypes.node]),
  /** class for calendar */
  className: PropTypes.string,
  /** the default value of the calendar */
  defaultValue: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string, PropTypes.array]),
  /** display days of previous or next month */
  displayAnotherMonth: PropTypes.bool,
  /** the last date that can be selected within the UI component. */
  max: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string, PropTypes.array]),
  /** the minimum date that can be selected within the UI component. */
  min: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string, PropTypes.array]),
  /** a callback function when click on a day of calendar */
  onClick: PropTypes.func,
  /** value of the calendar */
  value: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string, PropTypes.array])
};
export default Calendar;